// mikrotik.ts — FONTE UNICA dei dati MikroTik per le pagine Consulenza
// (/consulenza-mikrotik) e Corsi (/corsi-mikrotik): team con le
// certificazioni reali (numero, emittente, scadenza), link di verifica esterni,
// nomi estesi dei programmi e albero delle certificazioni con outline PDF.
// I certificati con dueDate passata vengono esclusi automaticamente dal
// rendering e dal JSON-LD (vedi getActiveCerts).

// ── Pagina Corsi MikroTik ──
// Live (senza calendario/prenotazioni: arrivano nello step 2, ADR 0016).
// Il flag resta come kill-switch: a false nasconde tutti i link "Scopri i corsi"
// nella pagina consulenza (banner cross-link + callout nelle card del team).
export const CORSI_PAGE_ENABLED = true
export const CORSI_URL = '/corsi-mikrotik'

// Link di verifica esterni MikroTik (pubblici)
export const MK_VERIFY = [
  { label: 'Lista Consultants MikroTik', href: 'https://mikrotik.com/consultants' },
  { label: 'MikroTik Certificate Search', href: 'https://mikrotik.com/certificates' },
] as const

// Nome breve del programma (per popover delle pill)
export const CERT_SHORT: Record<string, string> = {
  MTCNA: 'Network Associate',
  MTCWE: 'Wireless Engineer',
  MTCTCE: 'Traffic Control Engineer',
  MTCUME: 'User Management Engineer',
  MTCINE: 'Inter-networking Engineer',
  MTCIPv6E: 'IPv6 Engineer',
  MTCSE: 'Security Engineer',
  MTCSWE: 'Switching Engineer',
  MTCEWE: 'Enterprise Wireless Engineer',
  TRAINER: 'MikroTik Certified Trainer',
}

// Albero ufficiale dei programmi di certificazione (https://mikrotik.com/training/about):
// MTCNA è la base obbligatoria per tutti i track; MTCINE richiede in più MTCRE.
// Gli outline PDF ufficiali sono serviti same-origin da public/assets/corsi
// (apertura in nuova scheda: il viewer del browser permette lettura e download).
export interface CertTreeNode {
  code: string
  name: string
  icon: string // chiave di Icons
  pdf: string
  child?: CertTreeNode
}

export const MK_TREE_ROOT: CertTreeNode = {
  code: 'MTCNA', name: 'Network Associate', icon: 'router', pdf: '/assets/corsi/mtcna_outline_2019181161836.pdf',
}

export const MK_TREE_BRANCHES: CertTreeNode[] = [
  {
    code: 'MTCRE', name: 'Routing Engineer', icon: 'route', pdf: '/assets/corsi/mtcre_outline_2019181161836.pdf',
    child: { code: 'MTCINE', name: 'Inter-networking Engineer', icon: 'globe', pdf: '/assets/corsi/mtcine_outline_2020154114220.pdf' },
  },
  { code: 'MTCWE', name: 'Wireless Engineer', icon: 'wifi', pdf: '/assets/corsi/mtcwe_outline_2019181161836.pdf' },
  { code: 'MTCTCE', name: 'Traffic Control Engineer', icon: 'signal', pdf: '/assets/corsi/mtctce_outline_2020154113204.pdf' },
  { code: 'MTCUME', name: 'User Management Engineer', icon: 'headset', pdf: '/assets/corsi/mtcume_outline_2019181161836.pdf' },
  { code: 'MTCIPv6E', name: 'IPv6 Engineer', icon: 'globe', pdf: '/assets/corsi/mtcipv6e_outline_2019181161836.pdf' },
  { code: 'MTCSE', name: 'Security Engineer', icon: 'shield', pdf: '/assets/corsi/mtcse_outline_2019181161836.pdf' },
  { code: 'MTCSWE', name: 'Switching Engineer', icon: 'router', pdf: '/assets/corsi/mtcswe_outline_202083141326.pdf' },
  { code: 'MTCEWE', name: 'Enterprise Wireless Engineer', icon: 'wifi', pdf: '/assets/corsi/mtcewe_outline_202083141141.pdf' },
]

export interface MikrotikCertification {
  program: string // es. "MTCEWE", "TRAINER"
  certificateNo: string // es. "2309EWE3228"
  issuer: string // es. "MikroTik", "Spadhausen SRL Unipersonale"
  issueDate: string // ISO
  dueDate: string // ISO
}

export interface TeamMember {
  id: string
  initials: string
  name: string
  honorific?: string // titolo davanti al nome ("Dr."), separato per il JSON-LD honorificPrefix
  role: string
  bio: string
  consultantListed: boolean // presente nella lista consultants su mikrotik.com
  certifications: MikrotikCertification[]
}

export const MK_TEAM: TeamMember[] = [
  {
    id: 'elia-spadoni',
    initials: 'ES',
    name: 'Elia Spadoni',
    honorific: 'Dr.',
    role: 'Amministratore Unico',
    bio: '<strong>MikroTik Certified Trainer &amp; Consultant</strong>, specializzato in BGP e soluzioni ridondate per ISP e aziende, nel settore dal 2002. Presente nella lista ufficiale consultants su mikrotik.com.',
    consultantListed: true,
    certifications: [
      { program: 'TRAINER', certificateNo: 'TR0706', issuer: 'MikroTik', issueDate: '2022-05-27', dueDate: '2028-05-27' },
      { program: 'MTCEWE', certificateNo: '2309EWE3228', issuer: 'Starnet sh.p.k', issueDate: '2023-09-22', dueDate: '2026-09-22' },
      { program: 'MTCINE', certificateNo: '2309INE3099', issuer: 'Starnet sh.p.k', issueDate: '2023-09-21', dueDate: '2026-09-21' },
      { program: 'MTCIPv6E', certificateNo: '2309IPv6E2970', issuer: 'Starnet sh.p.k', issueDate: '2023-09-19', dueDate: '2026-09-19' },
      { program: 'MTCSE', certificateNo: '2309SE2952', issuer: 'Starnet sh.p.k', issueDate: '2023-09-18', dueDate: '2026-09-18' },
      { program: 'MTCSWE', certificateNo: '2309SWE3112', issuer: 'Starnet sh.p.k', issueDate: '2023-09-21', dueDate: '2026-09-21' },
      { program: 'MTCTCE', certificateNo: '2309TCE2969', issuer: 'Starnet sh.p.k', issueDate: '2023-09-19', dueDate: '2026-09-19' },
      { program: 'MTCUME', certificateNo: '2309UME2971', issuer: 'Starnet sh.p.k', issueDate: '2023-09-19', dueDate: '2026-09-19' },
      { program: 'MTCWE', certificateNo: '2309WE3096', issuer: 'Starnet sh.p.k', issueDate: '2023-09-21', dueDate: '2026-09-21' },
    ],
  },
  {
    id: 'manuel-calderoni',
    initials: 'MC',
    name: 'Manuel Calderoni',
    role: 'Responsabile Tecnico',
    bio: '<strong>MikroTik Certified</strong>, coordina il team tecnico lungo tutta la catena operativa: dall’assistenza al provisioning, fino al delivery dei servizi wholesale.',
    consultantListed: false,
    certifications: [
      { program: 'MTCSE', certificateNo: '2310SE6779', issuer: 'Spadhausen SRL Unipersonale', issueDate: '2023-10-31', dueDate: '2026-10-31' },
    ],
  },
  {
    id: 'alessio-di-somma',
    initials: 'AS',
    name: 'Alessio Di Somma',
    role: 'Responsabile Assistenza IT',
    bio: '<strong>MikroTik Certified</strong>, guida il team di assistenza tecnica che supporta i clienti privati e business.',
    consultantListed: false,
    certifications: [
      { program: 'MTCSE', certificateNo: '2310SE6778', issuer: 'Spadhausen SRL Unipersonale', issueDate: '2023-10-31', dueDate: '2026-10-31' },
    ],
  },
]

// I certificati scaduti spariscono da UI e JSON-LD alla prima build successiva
// alla scadenza (il sito è SSG: il filtro gira al prerender, non a runtime).
export const getActiveCerts = (m: TeamMember): MikrotikCertification[] =>
  m.certifications.filter((c) => new Date(c.dueDate) >= new Date())

// true se almeno una certificazione attiva è stata rilasciata da Spadhausen
// (prova che il corso ufficiale erogato da Spadhausen è certificante)
export const issuedBySpadhausen = (m: TeamMember): boolean =>
  getActiveCerts(m).some((c) => /spadhausen/i.test(c.issuer))
